/**
 * Book Campaign JavaScript
 * 1 Million Books Challenge
 */

(function() {
  'use strict';

  // Prevent double initialization if script is loaded multiple times
  if (window.BookCampaignInitialized) return;
  window.BookCampaignInitialized = true;

  // Campaign dates
  const CAMPAIGN_START = new Date('2026-02-01T00:00:00Z');
  const CAMPAIGN_END = new Date('2031-01-31T23:59:59Z');
  const CAMPAIGN_DURATION_MS = CAMPAIGN_END - CAMPAIGN_START;
  const GOAL_BOOKS = 1000000;
  const MAX_BOOKS_PER_SUBMISSION = 99;

  /**
   * Toast notification system
   */
  const Toast = {
    container: null,

    init: function() {
      this.container = document.getElementById('book-toast');
      if (!this.container) {
        this.container = document.createElement('div');
        this.container.id = 'book-toast';
        this.container.className = 'book-toast';
        document.body.appendChild(this.container);
      }
    },

    show: function(message, type = 'info', duration = 2000) {
      if (!this.container) this.init();
      
      this.container.textContent = message;
      this.container.className = 'book-toast book-toast--' + type;
      
      // Trigger reflow for animation
      this.container.offsetHeight;
      this.container.classList.add('show');

      setTimeout(() => {
        this.container.classList.remove('show');
      }, duration);
    },

    success: function(message) {
      this.show(message, 'success');
    },

    error: function(message) {
      this.show(message, 'error');
    }
  };

  /**
   * Book Counter
   */
  const BookCounter = {
    init: function(container) {
      if (!container) return;

      const apiUrl = container.dataset.apiUrl;
      if (!apiUrl) {
        console.error('Book Counter: No API URL provided');
        return;
      }

      this.container = container;
      this.booksBar = container.querySelector('.book-counter__bar-fill--books');
      this.timeBar = container.querySelector('.book-counter__bar-fill--time');
      this.booksCount = container.querySelector('.book-counter__books-count');
      this.booksPercent = container.querySelector('.book-counter__books-percent');
      this.timePercent = container.querySelector('.book-counter__time-percent');

      this.updateTimeProgress();
      this.fetchAndUpdate(apiUrl);

      // Update time progress every minute
      setInterval(() => this.updateTimeProgress(), 60000);
    },

    updateTimeProgress: function() {
      const now = new Date();
      const elapsed = Math.max(0, now - CAMPAIGN_START);
      const timePercent = Math.min(100, (elapsed / CAMPAIGN_DURATION_MS) * 100);
      
      if (this.timeBar) {
        this.timeBar.style.width = timePercent.toFixed(2) + '%';
      }
      if (this.timePercent) {
        this.timePercent.textContent = timePercent.toFixed(1) + '%';
      }

      this.currentTimePercent = timePercent;
      this.updateBookBarColor();
    },

    fetchAndUpdate: function(apiUrl) {
      fetch(apiUrl + '/total')
        .then(response => response.json())
        .then(data => {
          const totalBooks = data.total_books || 0;
          const booksPercent = Math.min(100, (totalBooks / GOAL_BOOKS) * 100);

          if (this.booksBar) {
            this.booksBar.style.width = booksPercent.toFixed(4) + '%';
          }
          if (this.booksCount) {
            this.booksCount.textContent = totalBooks.toLocaleString();
          }
          if (this.booksPercent) {
            this.booksPercent.textContent = booksPercent.toFixed(2) + '%';
          }

          this.currentBooksPercent = booksPercent;
          this.updateBookBarColor();
        })
        .catch(error => {
          console.error('Book Counter: Failed to fetch total', error);
        });
    },

    updateBookBarColor: function() {
      if (!this.booksBar || this.currentBooksPercent === undefined || this.currentTimePercent === undefined) {
        return;
      }

      this.booksBar.classList.remove('behind', 'ahead');

      if (this.currentBooksPercent < this.currentTimePercent) {
        this.booksBar.classList.add('behind'); // Red - behind schedule
      } else if (this.currentBooksPercent > this.currentTimePercent) {
        this.booksBar.classList.add('ahead'); // Green - ahead of schedule
      }
      // Otherwise stays blue (exactly on track)
    }
  };

  /**
   * Book Signup Form
   */
  const BookSignup = {
    init: function(container) {
      if (!container) return;

      this.container = container;
      this.form = container.querySelector('.book-signup__form');
      this.apiUrl = container.dataset.apiUrl;
      this.successMessage = container.dataset.successMessage || 'Thank you! Your books have been counted.';
      this.errorMessage = container.dataset.errorMessage || 'Something went wrong. Please try again.';
      this.rateLimitMessage = container.dataset.rateLimitMessage || "You've already submitted today. Try again tomorrow!";

      if (!this.form || !this.apiUrl) return;

      this.nameInput = this.form.querySelector('input[name="name"]');
      this.emailInput = this.form.querySelector('input[name="email"]');
      this.booksInput = this.form.querySelector('input[name="books_count"]');
      if (this.booksInput) {
        this.booksInput.readOnly = true;
        this.booksInput.addEventListener('keydown', (e) => e.preventDefault());
      }
      this.submitBtn = this.form.querySelector('.book-signup__submit');
      this.minusBtn = this.form.querySelector('.book-signup__number-btn--minus');
      this.plusBtn = this.form.querySelector('.book-signup__number-btn--plus');

      this.bindEvents();
    },

    bindEvents: function() {
      // Form submit
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));

      // Number buttons
      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', () => this.adjustBooks(-1));
      }
      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', () => this.adjustBooks(1));
      }

      this.updateNumberButtons();
    },

    adjustBooks: function(delta) {
      if (!this.booksInput) return;
      
      let current = parseInt(this.booksInput.value, 10) || 1;
      let newValue = current + delta;
      
      if (newValue < 1) newValue = 1;
      if (newValue > MAX_BOOKS_PER_SUBMISSION) newValue = MAX_BOOKS_PER_SUBMISSION;
      
      this.booksInput.value = newValue;
      this.updateNumberButtons();
    },

    updateNumberButtons: function() {
      if (this.minusBtn && this.booksInput) {
        const value = parseInt(this.booksInput.value, 10) || 1;
        this.minusBtn.disabled = value <= 1;
      }
      if (this.plusBtn && this.booksInput) {
        const value = parseInt(this.booksInput.value, 10) || 1;
        this.plusBtn.disabled = value >= MAX_BOOKS_PER_SUBMISSION;
      }
    },

    handleSubmit: async function(e) {
      e.preventDefault();

      const name = this.nameInput?.value?.trim();
      const email = this.emailInput?.value?.trim();
      const booksCount = parseInt(this.booksInput?.value, 10) || 1;

      // Basic validation
      if (!name || !email) {
        Toast.error('Please fill in all fields.');
        return;
      }

      if (!this.isValidEmail(email)) {
        Toast.error('Please enter a valid email address.');
        return;
      }

      if (booksCount > MAX_BOOKS_PER_SUBMISSION) {
        Toast.error('Try submitting less books at a time.');
        return;
      }

      // Disable button during submit
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Submitting...';
      }

      try {
        const response = await fetch(this.apiUrl + '/submissions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: name,
            email: email,
            books_count: booksCount
          })
        });

        if (response.status === 201) {
          Toast.success(this.successMessage);
          this.form.reset();
          this.booksInput.value = 1;
          this.updateNumberButtons();
          
          // Refresh counter if on same page
          const counter = document.querySelector('.book-counter');
          if (counter) {
            BookCounter.fetchAndUpdate(counter.dataset.apiUrl);
          }
        } else if (response.status === 429) {
          Toast.error(this.rateLimitMessage);
        } else {
          const data = await response.json().catch(() => ({}));
          Toast.error(data.detail || this.errorMessage);
        }
      } catch (error) {
        console.error('Book Signup: Submit failed', error);
        Toast.error(this.errorMessage);
      } finally {
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = this.container.dataset.buttonText || 'Submit';
        }
      }
    },

    isValidEmail: function(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
  };

  /**
   * Initialize on DOM ready
   */
  function init() {
    Toast.init();

    // Initialize all counters
    document.querySelectorAll('.book-counter').forEach(container => {
      BookCounter.init(container);
    });

    // Initialize all signup forms
    document.querySelectorAll('.book-signup').forEach(container => {
      BookSignup.init(container);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.BookCampaign = {
    Toast: Toast,
    Counter: BookCounter,
    Signup: BookSignup
  };

})();
